// Client state: session, authoritative view (from the host) and presented view (advanced by the
// Presentation Director beat by beat). UI components read `presented*`. docs/07 §8.2.
import { create } from 'zustand';
import type { AvailableActions, CardId, Command, LocationId, PlayerView, SeatId } from '@bcm/rules';
import { PROTOCOL_VERSION, type ClientMessage, type LobbyOp, type RoomSettings, type RoomSummary, type ServerMessage } from '@bcm/protocol';
import { WorkerConnection, WsConnection, onlineUrl, type ConnStatus, type Connection } from '../net/connection';
import { lastSession, playerName, playerToken, prefs, setLastSession, setPrefs } from '../app/identity';
import { errorText, problemResolution } from '../game/content';
import { setProblemResolutionLookup } from '../scene/cardFaces';
import { director } from '../director/director';
import { audio } from '../audio/sfx';

export interface LogEntry {
  seq: number;
  text: string;
  seat?: number;
  /** Cards the line names (spells, Action cards, Problems, Decrees…): inspectable in the Ledger. */
  defs?: string[];
}

export interface Toast {
  id: number;
  text: string;
  kind: 'info' | 'error' | 'good';
}

export interface Settings {
  /** Opponent animation speed multiplier (docs/06 §5.3). */
  opponentSpeed: number;
  followCamera: boolean;
  reducedMotion: boolean;
  showLog: boolean;
  /** 0–1 volumes (procedural audio, audio/sfx.ts). */
  volume: number;
  sfxVolume: number;
  ambienceVolume: number;
  /** Show coach marks (the guided first game). */
  coach: boolean;
  /** Coach marks already seen. */
  coachSeen: string[];
  /** Keep the screen from dimming and locking during a game (Screen Wake Lock, where supported). */
  keepAwake: boolean;
}

const DEFAULT_SETTINGS: Settings = {
  opponentSpeed: 1.5,
  followCamera: true,
  reducedMotion: false,
  showLog: true,
  volume: 0.8,
  sfxVolume: 0.8,
  ambienceVolume: 0.35,
  coach: true,
  coachSeen: [],
  keepAwake: true,
};

export interface GameStore {
  mode: 'online' | 'offline' | null;
  code: string | null;
  status: ConnStatus | 'idle';
  room: RoomSummary | null;
  you: { seat: SeatId | null; playerId: string } | null;

  view: PlayerView | null;
  actions: AvailableActions | null;
  seq: number;

  presented: PlayerView | null;
  presentedActions: AvailableActions | null;
  busy: boolean;

  log: LogEntry[];
  toasts: Toast[];
  chat: { seat: SeatId; phrase: number; at: number } | null;
  inFlight: boolean;
  clientSeq: number;
  selectedSpell: CardId | null;
  hoverLocation: LocationId | null;
  settings: Settings;

  createOnline(settings: Partial<RoomSettings>): Promise<void>;
  joinOnline(code: string): void;
  startOffline(bots: number, preset: 'v2' | 'v1', resolutions?: boolean): void;
  /** Go back to the match this device was last in (after a reload or relaunch). */
  resume(): Promise<boolean>;
  leave(): void;
  command(cmd: Command): void;
  undo(): void;
  lobby(op: LobbyOp): void;
  sendChat(phrase: number): void;
  toast(text: string, kind?: Toast['kind']): void;
  setSettings(s: Partial<Settings>): void;
  select(card: CardId | null): void;
}

let conn: Connection | null = null;
let toastId = 0;

export const useGame = create<GameStore>((set, get) => {
  const send = (msg: ClientMessage) => conn?.send(msg);

  const handlers = {
    onStatus: (status: ConnStatus) => set({ status }),
    onOpen: () => send({ t: 'hello', v: PROTOCOL_VERSION, token: playerToken(), name: playerName(), lastSeq: get().seq }),
    onMessage: (msg: ServerMessage) => handle(msg),
  };

  function handle(msg: ServerMessage): void {
    switch (msg.t) {
      case 'welcome':
        set({ you: msg.you, room: msg.room });
        break;
      case 'room':
        set({ room: msg.room });
        break;
      case 'sync':
        set({ view: msg.view, actions: msg.actions, seq: msg.seq, inFlight: false });
        director.snap(msg.view, msg.actions, msg.events);
        break;
      case 'batch':
        if (msg.seq <= get().seq) break; // duplicate
        set({ view: msg.view, actions: msg.actions, seq: msg.seq });
        director.enqueue(msg.events, msg.view, msg.actions);
        break;
      case 'ack':
        set({ inFlight: false });
        break;
      case 'err':
        set({ inFlight: false });
        get().toast(errorText(msg.error), 'error');
        if (msg.error.code === 'VERSION') conn?.close();
        break;
      case 'chat':
        set({ chat: { seat: msg.seat, phrase: msg.phrase, at: Date.now() } });
        break;
      case 'pong':
        break;
    }
  }

  function reset() {
    director.reset();
    set({
      room: null,
      you: null,
      view: null,
      actions: null,
      seq: 0,
      presented: null,
      presentedActions: null,
      log: [],
      inFlight: false,
      selectedSpell: null,
      busy: false,
    });
  }

  return {
    mode: null,
    code: null,
    status: 'idle',
    room: null,
    you: null,
    view: null,
    actions: null,
    seq: 0,
    presented: null,
    presentedActions: null,
    busy: false,
    log: [],
    toasts: [],
    chat: null,
    inFlight: false,
    clientSeq: 0,
    selectedSpell: null,
    hoverLocation: null,
    settings: { ...DEFAULT_SETTINGS, ...prefs<Partial<Settings>>('settings', {}) },

    async createOnline(settings) {
      const res = await fetch('/api/rooms', { method: 'POST', body: JSON.stringify(settings) });
      if (!res.ok) throw new Error(`Could not create a room (${res.status})`);
      const { code } = (await res.json()) as { code: string };
      history.pushState(null, '', `/room/${code}`);
      get().joinOnline(code);
    },

    joinOnline(code) {
      conn?.close();
      reset();
      set({ mode: 'online', code });
      setLastSession({ mode: 'online', code });
      conn = new WsConnection(onlineUrl(code), handlers);
    },

    startOffline(bots, preset, resolutions = true) {
      conn?.close();
      reset();
      set({ mode: 'offline', code: null });
      setLastSession({ mode: 'offline' });
      conn = new WorkerConnection({ maxPlayers: bots + 1, preset, resolutions }, {
        ...handlers,
        onOpen: () => {
          handlers.onOpen();
          for (let i = 0; i < bots; i++) send({ t: 'lobby', op: { op: 'addBot' } });
          send({ t: 'lobby', op: { op: 'start' } });
        },
      });
    },

    async resume() {
      const last = lastSession();
      if (!last || get().mode) return false;
      if (last.mode === 'online') {
        // Only rooms still in play: a finished or expired room just opens the menu.
        const res = await fetch(`/api/rooms/${last.code}`).catch(() => null);
        const room = res?.ok ? ((await res.json()) as { room: RoomSummary }).room : null;
        if (room?.status !== 'playing' || get().mode) {
          if (!room) setLastSession(null);
          return false;
        }
        history.replaceState(null, '', `/room/${last.code}`);
        get().joinOnline(last.code);
        return true;
      }
      reset();
      set({ mode: 'offline', code: null });
      const worker: WorkerConnection = new WorkerConnection({}, {
        ...handlers,
        onOpen: () => {
          if (worker.resumed) return handlers.onOpen();
          // Nothing saved to go back to.
          if (conn === worker) get().leave();
        },
      }, true);
      conn = worker;
      return true;
    },

    leave() {
      setLastSession(null);
      conn?.close();
      conn = null;
      reset();
      set({ mode: null, code: null, status: 'idle' });
      if (location.pathname !== '/') history.pushState(null, '', '/');
    },

    command(cmd) {
      const s = get();
      if (s.inFlight || s.busy) return;
      const clientSeq = s.clientSeq + 1;
      set({ inFlight: true, clientSeq, selectedSpell: null });
      if (cmd.t === 'place' && s.you?.seat != null) director.optimisticPlace(s.you.seat, cmd.location);
      send({ t: 'cmd', clientSeq, cmd });
    },

    undo() {
      const s = get();
      if (s.inFlight) return;
      const clientSeq = s.clientSeq + 1;
      set({ inFlight: true, clientSeq });
      send({ t: 'undo', clientSeq });
    },

    lobby(op) {
      send({ t: 'lobby', op });
    },

    sendChat(phrase) {
      send({ t: 'chat', phrase });
    },

    toast(text, kind = 'info') {
      const id = ++toastId;
      set((s) => ({ toasts: [...s.toasts.slice(-3), { id, text, kind }] }));
      setTimeout(() => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })), 3200);
    },

    setSettings(partial) {
      const settings = { ...get().settings, ...partial };
      setPrefs('settings', settings);
      set({ settings });
      audio.setVolumes({ master: settings.volume, sfx: settings.sfxVolume, ambience: settings.ambienceVolume });
    },

    select(card) {
      set({ selectedSpell: card });
    },
  };
});

/** Convenience selectors. */
export const mySeat = (s: GameStore): SeatId | null => s.you?.seat ?? null;
export const seatName = (s: GameStore, seat: number): string => s.room?.seats[seat]?.name ?? s.presented?.seats[seat]?.name ?? `Seat ${seat + 1}`;

// Debug handle for development builds (inspect state from the console / automated tests).
if (import.meta.env.DEV) (globalThis as unknown as { __bcm: typeof useGame }).__bcm = useGame;

// City Problem faces show the Resolution each carries in the game on the table (P-17).
setProblemResolutionLookup((problem) => problemResolution(useGame.getState().presented, problem));

// Apply stored volumes at start-up.
{
  const s = useGame.getState().settings;
  audio.setVolumes({ master: s.volume, sfx: s.sfxVolume, ambience: s.ambienceVolume });
}
